import React from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { AuthProvider } from "./auth/AuthProvider";
import { NO_ACCESS_ROUTE, useRoles } from "./auth/access";
import RequireRole from "./auth/RequireRole";
import ErrorBoundary from "./components/ErrorBoundary";
import Layout from "./components/Layout";
import { PreferencesProvider } from "./preferences/PreferencesProvider";
import ComplianceDashboardPage from "./pages/ComplianceDashboardPage";
import WorkspacesPage from "./pages/WorkspacesPage";
import WorkspaceDetailPage from "./pages/WorkspaceDetailPage";
import ReportsPage from "./pages/ReportsPage";
import ReportRedirectPage from "./pages/ReportRedirectPage";
import ArtifactDetailPage from "./pages/ArtifactDetailPage";
import ActivityPage from "./pages/ActivityPage";
import AuditLogPage from "./pages/AuditLogPage";
import PrivilegeChangesPage from "./pages/PrivilegeChangesPage";
import AccessChangesPage from "./pages/AccessChangesPage";
import RiskPage from "./pages/RiskPage";
import AccessControlPage from "./pages/AccessControlPage";
import DeploymentsPage from "./pages/DeploymentsPage";
import ReconciliationPage from "./pages/ReconciliationPage";
import RefreshHealthPage from "./pages/RefreshHealthPage";
import EDiscoveryPage from "./pages/EDiscoveryPage";
import PimActivationsPage from "./pages/PimActivationsPage";
import DataflowsPage from "./pages/DataflowsPage";
import GatewaysPage from "./pages/GatewaysPage";
import InfoPage from "./pages/InfoPage";
import NoAccessPage from "./pages/NoAccessPage";
import SettingsPage from "./pages/SettingsPage";
import SystemLogsPage from "./pages/SystemLogsPage";

/**
 * A route gated by the signed-in user's Entra app role.
 *
 * The guard is handed the same `path` string it registers, so the route and
 * the permission cannot drift apart — which is the failure mode of keeping a
 * separate list of protected paths.
 */
const guarded = (path: string, element: React.ReactNode) => (
  <Route key={path} path={path} element={<RequireRole route={path}>{element}</RequireRole>} />
);

function AppRoutes() {
  const location = useLocation();
  const { homePath } = useRoles();

  return (
    <Layout>
      {/* Scoped inside Layout so a page-level crash keeps the navigation
          usable, and keyed on the path so navigating away clears the error. */}
      <ErrorBoundary label="this page" resetKey={location.pathname}>
        <Routes>
          {guarded("/", <ComplianceDashboardPage />)}
          {guarded("/audit-log", <AuditLogPage />)}
          {guarded("/privilege-changes", <PrivilegeChangesPage />)}
          {guarded("/access-changes", <AccessChangesPage />)}
          {guarded("/risk", <RiskPage />)}
          {guarded("/access-control", <AccessControlPage />)}
          {guarded("/deployments", <DeploymentsPage />)}
          {guarded("/refresh-health", <RefreshHealthPage />)}
          {guarded("/workspaces", <WorkspacesPage />)}
          {guarded("/workspaces/:workspaceId", <WorkspaceDetailPage />)}
          {guarded("/workspaces/:workspaceId/reports", <ReportsPage />)}
          {/* Reports, datasets, dataflows, dashboards and apps all land here.
              Workspace-scoped because a dashboard has no stored row and can
              only be read live, which needs the workspace to read it from. */}
          {guarded(
            "/workspaces/:workspaceId/artifacts/:artifactType/:artifactId",
            <ArtifactDetailPage />
          )}
          {guarded("/reports", <ReportsPage />)}
          {/* Legacy: resolves the workspace, then forwards to the unified
              artifact page. Kept so existing bookmarks and evidence links
              do not break. */}
          {guarded("/reports/:reportId", <ReportRedirectPage />)}
          {guarded("/activity", <ActivityPage />)}
          {guarded("/reconciliation", <ReconciliationPage />)}
          {guarded("/ediscovery", <EDiscoveryPage />)}
          {guarded("/pim-activations", <PimActivationsPage />)}
          {guarded("/dataflows", <DataflowsPage />)}
          {guarded("/gateways", <GatewaysPage />)}
          {guarded("/info", <InfoPage />)}
          {guarded("/settings", <SettingsPage />)}
          {guarded("/system-logs", <SystemLogsPage />)}
          {/* Ungated on purpose: this is where a user with no role is sent, so
              guarding it would be a redirect loop. */}
          <Route path={NO_ACCESS_ROUTE} element={<NoAccessPage />} />
          {/* MSAL redirect callback — renders blank while MSAL processes the auth code */}
          <Route path="/auth/callback" element={<></>} />
          {/* Catch-all. Goes to the role's own landing page, not "/", because
              "/" is Admin-only and would bounce a manager straight back. */}
          <Route path="*" element={<Navigate to={homePath} replace />} />
        </Routes>
      </ErrorBoundary>
    </Layout>
  );
}

export default function App() {
  return (
    // The outermost boundary is the last line of defence: without it a throw
    // from a provider or from Layout itself unmounts the whole tree and leaves
    // a blank white page with no way back apart from a manual reload.
    <ErrorBoundary label="the application">
      <BrowserRouter>
        <AuthProvider>
          {/* Inside AuthProvider: loading preferences needs an access token. */}
          <PreferencesProvider>
            <AppRoutes />
          </PreferencesProvider>
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}
